// ============================================
// DEManyproduct.jsx - Digital Engineering & Manufacturing
// Version: 5.0 - Enhanced AI Integration
// ============================================

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import {
  Sparkles, Wand2, Factory, Package, Wrench, Cpu, Users, Target,
  FileText, Settings, Zap, Info, Eye, Layers, Box, RefreshCw,
  Upload, AlertTriangle, CheckCircle2, X, FileSpreadsheet, FileJson,
  Cog, Microscope, Flame, Activity, TrendingUp, PieChart,
  BarChart3, Clock, Database, Server, Cloud, Network,
  Maximize2, Minimize2, Search, Filter, List, Sliders,
  Gauge, Battery, Sun, Moon, Star, Award, BookOpen,
  ChevronDown, ChevronRight, ChevronUp, ArrowUpRight,
  ExternalLink, Save, RotateCcw, ZoomIn, ZoomOut, Table2, Code,
  Home, Move, GitMerge, Workflow, Route, MapPin, Navigation,
  Compass, Anchor, Crosshair, Hash, AlertOctagon, Ban,
  ShieldOff, Lock, Unlock, Key, Fingerprint, Scan,
  TreePine, GitFork, ArrowLeftRight, Columns, GanttChartSquare,
  Kanban, LayoutDashboard, Edit3, GitGraph, FolderTree, Shield,
  ShieldCheck, ShieldAlert, GitPullRequest, GitCommit,
  FileCode, Archive, FolderArchive, Filter as FilterIcon,
  Grid, List as ListIcon, TrendingDown, TrendingUp as TrendingUpIcon,
  BarChart, PieChart as PieChartIcon, EyeOff, RotateCw,
  RotateCcw as RotateCcwIcon, RefreshCw as RefreshCwIcon,
  Maximize as MaximizeIcon, Minimize as MinimizeIcon,
  Volume2, VolumeX, Play, Pause, SkipForward, SkipBack,
  ChevronDown as ChevronDownIcon, Link, Unlink, Wifi, WifiOff,
  Fingerprint as FingerprintIcon,
  Globe, Server as ServerIcon, Database as DatabaseIcon, Download
} from 'lucide-react';

// ============================================
// THEME
// ============================================
const T = {
  bg:'#0a0e17', s:'#111827', s2:'#1a2235', s3:'#1e293b', b:'#1e293b',
  t:'#e2e8f0', t2:'#94a3b8', t3:'#64748b', p:'#3b82f6', pl:'#60a5fa',
  g:'#10b981', gl:'#34d399', w:'#f59e0b', wl:'#fbbf24', d:'#ef4444',
  dl:'#f87171', pp:'#8b5cf6', ppl:'#a78bfa', te:'#14b8a6', tel:'#2dd4bf',
  pk:'#ec4899', pkl:'#f472b6', cy:'#06b6d4', in:'#6366f1', or:'#f97316'
};

// ============================================
// STYLES
// ============================================
const S = {
  container: { minHeight: '100vh', background: `linear-gradient(135deg, ${T.bg} 0%, #1a1f2e 50%, #0f1419 100%)`, fontFamily: "'Inter','Segoe UI',system-ui,sans-serif", color: T.t, padding: '20px' },
  card: { background: T.s2, border: `1px solid ${T.b}`, borderRadius: '12px', padding: '20px', marginBottom: '16px' },
  header: { fontSize: '1.5rem', fontWeight: 700, background: `linear-gradient(135deg, ${T.pl}, ${T.tel})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: '8px' },
  subHeader: { fontSize: '0.9rem', color: T.t2, marginBottom: '16px' },
  grid: (n) => ({ display: 'grid', gridTemplateColumns: `repeat(${n}, 1fr)`, gap: '12px' }),
  input: { width: '100%', padding: '8px 12px', background: T.bg, border: `1px solid ${T.b}`, borderRadius: '6px', color: T.t, fontSize: '0.85rem', outline: 'none', transition: 'all 0.2s' },
  textarea: { width: '100%', padding: '8px 12px', background: T.bg, border: `1px solid ${T.b}`, borderRadius: '6px', color: T.t, fontSize: '0.85rem', outline: 'none', minHeight: '120px', fontFamily: 'inherit', resize: 'vertical', transition: 'all 0.2s' },
  select: { width: '100%', padding: '8px 12px', background: T.bg, border: `1px solid ${T.b}`, borderRadius: '6px', color: T.t, fontSize: '0.85rem', outline: 'none', cursor: 'pointer' },
  label: { fontSize: '0.7rem', color: T.t2, marginBottom: '4px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' },
  btn: { padding: '8px 16px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px', transition: 'all 0.2s' },
  bPri: { background: `linear-gradient(135deg, ${T.p}, #2563eb)`, color: 'white' },
  bSuc: { background: `linear-gradient(135deg, ${T.g}, #059669)`, color: 'white' },
  bWar: { background: `linear-gradient(135deg, ${T.w}, #d97706)`, color: 'white' },
  bDan: { background: `linear-gradient(135deg, ${T.d}, #dc2626)`, color: 'white' },
  bGho: { background: 'transparent', color: T.t2, border: `1px solid ${T.b}` },
  bPP: { background: `linear-gradient(135deg, ${T.pp}, #7c3aed)`, color: 'white' },
  bTeal: { background: `linear-gradient(135deg, ${T.te}, #0d9488)`, color: 'white' },
  statCard: { background: T.s, border: `1px solid ${T.b}`, borderRadius: '8px', padding: '12px', textAlign: 'center' },
  statVal: { fontSize: '1.5rem', fontWeight: 700, color: T.pl },
  statLbl: { fontSize: '0.6rem', color: T.t3, textTransform: 'uppercase' },
  badge: (c) => ({ display: 'inline-block', padding: '2px 8px', borderRadius: '4px', fontSize: '0.65rem', fontWeight: 600, background: `${c}22`, color: c }),
  progressBar: { height: '6px', background: T.b, borderRadius: '3px', overflow: 'hidden', marginTop: '8px' },
  progressFill: { height: '100%', borderRadius: '3px', transition: 'width 0.3s ease' },
  modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modalC: { background: T.s2, borderRadius: '12px', padding: '24px', maxWidth: '90vw', maxHeight: '90vh', overflow: 'auto', border: `1px solid ${T.b}`, boxShadow: '0 20px 60px rgba(0,0,0,0.5)' },
  tbl: { width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' },
  th: { textAlign: 'left', padding: '6px 8px', color: T.t2, fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', borderBottom: `2px solid ${T.b}` },
  td: { padding: '6px 8px', borderBottom: `1px solid ${T.b}` },
  scrollable: { maxHeight: '300px', overflowY: 'auto' }
};

// ============================================
// PRODUCT TEMPLATES DATABASE
// ============================================
const PRODUCT_TEMPLATES = {
  'aluminum-bracket': {
    name: '🔩 Aluminum Mounting Bracket',
    description: `A precision machined aluminum bracket assembly for industrial equipment mounting. 
The product consists of a main aluminum bracket body with four mounting holes, two reinforcement ribs, 
and stainless steel hardware. Key specifications include:
- Material: 6061-T6 Aluminum
- Dimensions: 200mm x 150mm x 25mm
- Tolerance: ±0.05mm on critical dimensions
- Surface finish: Anodized per MIL-A-8625 Type II
- Estimated annual volume: 1000 units
- Required certifications: ISO 9001:2015
- Assembly required: 4 x M8 stainless steel bolts with washers
- Weight: Approximately 1.2 kg
- Applications: Industrial equipment, machinery mounting, structural support`,
    industry: 'Industrial',
    complexity: 5,
    quantity: 1000,
    materials: ['Aluminum 6061-T6', 'Stainless Steel'],
    processes: ['CNC Machining', 'Anodizing', 'Assembly']
  },
  'steel-frame': {
    name: '🏗️ Steel Structural Frame',
    description: `A heavy-duty steel structural frame for industrial machinery support.
The product consists of a welded steel frame construction with precision machined mounting surfaces.
Key specifications include:
- Material: A36 Structural Steel
- Dimensions: 1200mm x 800mm x 400mm
- Tolerance: ±0.5mm on critical dimensions
- Surface finish: Industrial paint coating
- Estimated annual volume: 500 units
- Required certifications: AWS D1.1 Welding
- Assembly required: Complete welded structure
- Weight: Approximately 85 kg
- Applications: Heavy machinery, industrial equipment, structural support`,
    industry: 'Heavy Industrial',
    complexity: 7,
    quantity: 500,
    materials: ['A36 Steel', 'SAE Bolts'],
    processes: ['Cutting', 'Welding', 'Machining', 'Painting']
  },
  'plastic-housing': {
    name: '📦 Plastic Electronics Housing',
    description: `An injection molded plastic housing for consumer electronics.
The product consists of a two-piece snap-fit housing with internal mounting bosses.
Key specifications include:
- Material: ABS Plastic
- Dimensions: 150mm x 100mm x 40mm
- Tolerance: ±0.1mm on critical dimensions
- Surface finish: Textured matte finish
- Estimated annual volume: 10000 units
- Required certifications: RoHS Compliant
- Assembly required: Snap-fit assembly with screws
- Weight: Approximately 0.15 kg
- Applications: Consumer electronics, IoT devices, control boxes`,
    industry: 'Consumer Electronics',
    complexity: 4,
    quantity: 10000,
    materials: ['ABS Plastic', 'Stainless Steel Screws'],
    processes: ['Injection Molding', 'Assembly', 'Inspection']
  },
  'shaft-rotor': {
    name: '⚙️ Precision Motor Shaft',
    description: `A precision ground and hardened steel motor shaft for electric motors.
The product consists of a cylindrical shaft with keyways and precision bearing surfaces.
Key specifications include:
- Material: 4140 Chromoly Steel
- Dimensions: 50mm diameter x 300mm length
- Tolerance: ±0.005mm on bearing surfaces
- Surface finish: Ground to Ra 0.2
- Estimated annual volume: 2000 units
- Required certifications: ISO 9001:2015
- Assembly required: Keyway broaching
- Weight: Approximately 4.5 kg
- Applications: Electric motors, generators, pumps`,
    industry: 'Automotive',
    complexity: 8,
    quantity: 2000,
    materials: ['4140 Steel', 'Hardening Compound'],
    processes: ['Turning', 'Grinding', 'Heat Treatment', 'Keyway Broaching']
  },
  'medical-implant': {
    name: '🏥 Medical Titanium Implant',
    description: `A titanium alloy medical implant for orthopedic applications.
The product consists of a precision machined implant with porous coating for bone ingrowth.
Key specifications include:
- Material: Ti-6Al-4V Titanium
- Dimensions: Custom patient-specific
- Tolerance: ±0.005mm on critical dimensions
- Surface finish: Porous coating and polished surfaces
- Estimated annual volume: 500 units
- Required certifications: ISO 13485, FDA 21 CFR Part 820
- Assembly required: None (single piece)
- Weight: Approximately 0.02-0.05 kg
- Applications: Orthopedic implants, dental implants, surgical instruments`,
    industry: 'Medical',
    complexity: 9,
    quantity: 500,
    materials: ['Ti-6Al-4V Titanium', 'Medical Grade'],
    processes: ['CNC Machining', 'EDM', 'Polishing', 'Inspection']
  },
  'composite-panel': {
    name: '🛡️ Composite Aerospace Panel',
    description: `A carbon fiber composite panel for aerospace applications.
The product consists of a honeycomb sandwich structure with carbon fiber skins.
Key specifications include:
- Material: Carbon Fiber Reinforced Polymer
- Dimensions: 1000mm x 500mm x 20mm
- Tolerance: ±0.1mm on critical dimensions
- Surface finish: Autoclave cured Class A finish
- Estimated annual volume: 200 units
- Required certifications: AS9100D
- Assembly required: None (single piece)
- Weight: Approximately 2.5 kg
- Applications: Aircraft interiors, UAV structures, lightweight panels`,
    industry: 'Aerospace',
    complexity: 9,
    quantity: 200,
    materials: ['Carbon Fiber', 'Honeycomb Core', 'Epoxy Resin'],
    processes: ['Layup', 'Autoclave Curing', 'NDI Inspection']
  },
  'consumer-product': {
    name: '📱 Consumer Electronics Enclosure',
    description: `A consumer electronics enclosure with high-end finish and multi-material construction.
The product consists of a dual-shot molded plastic housing with metal inserts.
Key specifications include:
- Material: Polycarbonate/ABS blend
- Dimensions: 120mm x 70mm x 15mm
- Tolerance: ±0.05mm on critical dimensions
- Surface finish: Soft-touch coating with UV protection
- Estimated annual volume: 50000 units
- Required certifications: UL 94 V-0, RoHS
- Assembly required: Ultrasonic welding
- Weight: Approximately 0.08 kg
- Applications: Smart devices, wearables, consumer electronics`,
    industry: 'Consumer',
    complexity: 6,
    quantity: 50000,
    materials: ['PC/ABS Blend', 'Stainless Steel Inserts'],
    processes: ['Injection Molding', 'Ultrasonic Welding', 'Coating']
  },
  'pump-housing': {
    name: '🔄 Industrial Pump Housing',
    description: `A cast and machined pump housing for industrial fluid handling.
The product consists of a sand-cast housing with precision machined sealing surfaces.
Key specifications include:
- Material: Cast Iron
- Dimensions: 300mm x 250mm x 200mm
- Tolerance: ±0.1mm on critical dimensions
- Surface finish: High-gloss bore finish
- Estimated annual volume: 1000 units
- Required certifications: API Q1
- Assembly required: O-ring installation
- Weight: Approximately 15 kg
- Applications: Pumps, valves, fluid handling systems`,
    industry: 'Energy',
    complexity: 7,
    quantity: 1000,
    materials: ['Cast Iron', 'Stainless Steel Hardware'],
    processes: ['Casting', 'CNC Machining', 'Inspection']
  }
};

// ============================================
// AI MODEL CONFIGURATION
// ============================================
const AI_MODELS = {
  'gpt-4': {
    name: 'GPT-4',
    provider: 'OpenAI',
    requiresApiKey: true,
    apiKeyPlaceholder: 'sk-...',
    description: 'Advanced AI model with superior reasoning',
    maxTokens: 4000,
    costPer1000Tokens: 0.03,
    features: ['Manufacturing Expert', 'Quality Management', 'Process Planning']
  },
  'claude-3': {
    name: 'Claude 3 Opus',
    provider: 'Anthropic',
    requiresApiKey: true,
    apiKeyPlaceholder: 'sk-ant-...',
    description: 'Balanced AI with strong analytical capabilities',
    maxTokens: 4000,
    costPer1000Tokens: 0.015,
    features: ['Material Selection', 'Sustainability', 'Cost Optimization']
  },
  'llama-2': {
    name: 'Llama 2',
    provider: 'Meta (Open Source)',
    requiresApiKey: false,
    apiKeyPlaceholder: 'Optional',
    description: 'Open source AI model for offline use',
    maxTokens: 2048,
    costPer1000Tokens: 0,
    features: ['Basic Analysis', 'Pattern Recognition', 'Data Extraction']
  },
  'gemini': {
    name: 'Gemini Pro',
    provider: 'Google',
    requiresApiKey: true,
    apiKeyPlaceholder: 'AIza...',
    description: 'Google\'s latest multimodal AI',
    maxTokens: 4000,
    costPer1000Tokens: 0.02,
    features: ['Multi-modal Understanding', 'Technical Analysis', 'Optimization']
  },
  'offline': {
    name: 'Offline Mode',
    provider: 'Local Engine',
    requiresApiKey: false,
    apiKeyPlaceholder: 'N/A',
    description: 'Built-in generation engine (no internet required)',
    maxTokens: 1000,
    costPer1000Tokens: 0,
    features: ['Full Offline Capability', 'Basic Generation', 'Pre-built Templates']
  }
};

// ============================================
// AI CONNECTION MANAGER
// ============================================
class AIConnectionManager {
  constructor() {
    this.connectionStatus = {};
    this.testResults = {};
  }

  async testConnection(model, apiKey) {
    const startTime = Date.now();
    try {
      const config = AI_MODELS[model];
      if (!config) {
        return {
          success: false,
          error: 'Unknown AI model selected',
          latency: 0,
          timestamp: new Date().toISOString()
        };
      }

      // For offline mode, always succeed
      if (model === 'offline') {
        return {
          success: true,
          error: null,
          latency: 0,
          timestamp: new Date().toISOString(),
          message: 'Offline mode is always available'
        };
      }

      // Check if API key is required and provided
      if (config.requiresApiKey && !apiKey) {
        return {
          success: false,
          error: 'API key is required for this model',
          latency: 0,
          timestamp: new Date().toISOString()
        };
      }

      // Simulate connection test with timeout
      const testPromise = new Promise((resolve) => {
        setTimeout(() => {
          // Simulate success/failure based on API key format
          const isValidKey = apiKey && apiKey.length > 10;
          if (isValidKey) {
            resolve({
              success: true,
              error: null,
              latency: Math.floor(Math.random() * 200 + 100),
              timestamp: new Date().toISOString(),
              message: `Connected to ${config.name} successfully`
            });
          } else {
            resolve({
              success: false,
              error: 'Invalid API key format. Please check your API key.',
              latency: 0,
              timestamp: new Date().toISOString()
            });
          }
        }, 1000);
      });

      const result = await Promise.race([
        testPromise,
        new Promise((resolve) => setTimeout(() => resolve({
          success: false,
          error: 'Connection timeout (5 seconds)',
          latency: 5000,
          timestamp: new Date().toISOString()
        }), 5000))
      ]);

      this.connectionStatus[model] = result;
      return result;
    } catch (error) {
      const result = {
        success: false,
        error: error.message || 'Unknown connection error',
        latency: Date.now() - startTime,
        timestamp: new Date().toISOString()
      };
      this.connectionStatus[model] = result;
      return result;
    }
  }

  getConnectionStatus(model) {
    return this.connectionStatus[model] || null;
  }

  getConnectionStatusUI(model) {
    const status = this.getConnectionStatus(model);
    if (!status) {
      return { icon: '⚪', color: T.t3, text: 'Not Tested' };
    }
    if (status.success) {
      return { icon: '🟢', color: T.g, text: `Connected (${status.latency}ms)` };
    }
    return { icon: '🔴', color: T.d, text: 'Failed' };
  }
}

// ============================================
// AI ASSISTANT SERVICE
// ============================================
class AIService {
  constructor(model, apiKey) {
    this.model = model;
    this.apiKey = apiKey;
    this.config = AI_MODELS[model] || AI_MODELS['offline'];
    this.isConnected = false;
    this.connectionManager = new AIConnectionManager();
  }

  async connect() {
    const result = await this.connectionManager.testConnection(this.model, this.apiKey);
    this.isConnected = result.success;
    return result;
  }

  async analyzeProduct(description) {
    if (this.model === 'offline' || !this.isConnected) {
      return this.getOfflineAnalysis(description);
    }

    try {
      // Simulate AI API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // In production, this would be a real API call
      return {
        success: true,
        analysis: {
          productType: this.extractProductType(description),
          industry: this.extractIndustry(description),
          complexity: this.estimateComplexity(description),
          estimatedMaterials: this.extractMaterials(description),
          recommendedProcesses: this.extractProcesses(description),
          qualityRequirements: ['ISO 9001', 'Visual Inspection', 'Dimensional Inspection'],
          estimatedCostPerUnit: 100 + Math.random() * 400,
          manufacturingTime: 2 + Math.random() * 5,
          specialConsiderations: ['Standard tolerances ±0.05mm']
        },
        model: this.model,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        fallbackAnalysis: this.getOfflineAnalysis(description)
      };
    }
  }

  async generateArtifacts(description, strategy, type) {
    if (this.model === 'offline' || !this.isConnected) {
      return this.getOfflineArtifacts(description, strategy);
    }

    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      return {
        success: true,
        artifacts: {
          ebom: [],
          mbom: [],
          bop: { productBOP: { processes: [] } },
          equipment: [],
          tools: [],
          skills: [],
          qualityPlans: [],
          ewi: []
        },
        model: this.model,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        fallbackArtifacts: this.getOfflineArtifacts(description, strategy)
      };
    }
  }

  async validateData(artifacts, strategy) {
    if (this.model === 'offline' || !this.isConnected) {
      return this.getOfflineValidation(artifacts);
    }

    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      return {
        success: true,
        validation: {
          isValid: true,
          issues: [],
          warnings: ['AI validation performed with model: ' + this.model],
          suggestions: ['Consider adding more detailed material specifications'],
          optimizationOpportunities: ['Review machining tolerances'],
          qualityScore: 85 + Math.floor(Math.random() * 15)
        },
        model: this.model,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        fallbackValidation: this.getOfflineValidation(artifacts)
      };
    }
  }

  // Offline methods
  getOfflineAnalysis(description) {
    return {
      success: true,
      analysis: {
        productType: 'Industrial Component (Offline)',
        industry: 'General Manufacturing',
        complexity: 5,
        estimatedMaterials: ['Aluminum', 'Steel', 'Stainless'],
        recommendedProcesses: ['CNC Machining', 'Assembly', 'Inspection'],
        qualityRequirements: ['ISO 9001', 'Visual Inspection', 'Dimensional Inspection'],
        estimatedCostPerUnit: 150 + Math.random() * 100,
        manufacturingTime: 2 + Math.random() * 3,
        specialConsiderations: ['Standard tolerances ±0.05mm (Offline Mode)']
      },
      model: 'offline',
      timestamp: new Date().toISOString()
    };
  }

  getOfflineArtifacts(description, strategy) {
    return {
      success: true,
      artifacts: {
        ebom: [],
        mbom: [],
        bop: { productBOP: { processes: [] } },
        equipment: [],
        tools: [],
        skills: [],
        qualityPlans: [],
        ewi: []
      },
      model: 'offline',
      timestamp: new Date().toISOString()
    };
  }

  getOfflineValidation(artifacts) {
    return {
      success: true,
      validation: {
        isValid: true,
        issues: [],
        warnings: ['Data generated in offline mode'],
        suggestions: ['Consider connecting to AI for enhanced validation'],
        optimizationOpportunities: ['Review for completeness'],
        qualityScore: 75
      },
      model: 'offline',
      timestamp: new Date().toISOString()
    };
  }

  // Helper methods for extraction
  extractProductType(description) {
    const types = ['bracket', 'frame', 'housing', 'shaft', 'panel', 'enclosure', 'implant', 'component'];
    for (const type of types) {
      if (description.toLowerCase().includes(type)) return type;
    }
    return 'component';
  }

  extractIndustry(description) {
    const industries = {
      'aerospace': ['aircraft', 'flight', 'wing', 'fuselage'],
      'automotive': ['car', 'vehicle', 'motor', 'shaft'],
      'medical': ['implant', 'surgical', 'orthopedic', 'dental'],
      'consumer': ['electronics', 'device', 'consumer', 'enclosure'],
      'industrial': ['machinery', 'industrial', 'equipment', 'mounting']
    };
    for (const [industry, keywords] of Object.entries(industries)) {
      for (const keyword of keywords) {
        if (description.toLowerCase().includes(keyword)) return industry;
      }
    }
    return 'general';
  }

  estimateComplexity(description) {
    const words = description.split(' ');
    const technicalTerms = ['precision', 'tolerance', 'dimension', 'material', 'specification', 'certification', 'finish', 'coating'];
    let score = 3;
    for (const term of technicalTerms) {
      if (description.toLowerCase().includes(term)) score += 0.5;
    }
    return Math.min(10, Math.round(score + (words.length / 50)));
  }

  extractMaterials(description) {
    const materials = [];
    const materialKeywords = ['aluminum', 'steel', 'titanium', 'stainless', 'plastic', 'composite', 'carbon', 'iron', 'copper'];
    for (const material of materialKeywords) {
      if (description.toLowerCase().includes(material)) {
        materials.push(material.charAt(0).toUpperCase() + material.slice(1));
      }
    }
    return materials.length > 0 ? materials : ['Aluminum', 'Steel'];
  }

  extractProcesses(description) {
    const processes = [];
    const processKeywords = ['machining', 'casting', 'welding', 'assembly', 'injection', 'molding', 'grinding', 'turning', 'milling'];
    for (const process of processKeywords) {
      if (description.toLowerCase().includes(process)) {
        processes.push(process.charAt(0).toUpperCase() + process.slice(1));
      }
    }
    return processes.length > 0 ? processes : ['CNC Machining', 'Assembly'];
  }
}

// ============================================
// MANUFACTURING KNOWLEDGE BASE (Condensed)
// ============================================
const ManufacturingKnowledgeBase = {
  materials: {
    metals: {
      steel: { density: 7.85, machinability: 0.7, weldability: 0.8, costPerKg: 2.5 },
      aluminum: { density: 2.7, machinability: 0.9, weldability: 0.6, costPerKg: 3.2 },
      titanium: { density: 4.5, machinability: 0.3, weldability: 0.4, costPerKg: 25 },
      stainless: { density: 8.0, machinability: 0.5, weldability: 0.9, costPerKg: 6.0 }
    },
    composites: {
      carbonFiber: { density: 1.6, machinability: 0.3, weldability: 0, costPerKg: 40 },
      fiberglass: { density: 2.0, machinability: 0.4, weldability: 0, costPerKg: 5 }
    },
    plastics: {
      abs: { density: 1.05, machinability: 0.8, weldability: 0.5, costPerKg: 3.0 },
      nylon: { density: 1.14, machinability: 0.7, weldability: 0.3, costPerKg: 4.5 },
      polycarbonate: { density: 1.2, machinability: 0.6, weldability: 0.2, costPerKg: 5.0 }
    }
  },
  processes: {
    subtractive: {
      cncMilling: { precision: 0.01, maxSize: 2000, costPerHour: 100 },
      cncTurning: { precision: 0.01, maxSize: 500, costPerHour: 90 },
      grinding: { precision: 0.001, maxSize: 500, costPerHour: 80 }
    },
    forming: {
      casting: { precision: 0.5, maxSize: 3000, costPerHour: 60 },
      forging: { precision: 0.3, maxSize: 2000, costPerHour: 70 },
      stamping: { precision: 0.1, maxSize: 1000, costPerHour: 50 }
    },
    joining: {
      welding: { precision: 0.5, maxSize: 5000, costPerHour: 60 },
      adhesiveBonding: { precision: 0.2, maxSize: 3000, costPerHour: 40 }
    },
    finishing: {
      painting: { precision: 0.1, maxSize: 3000, costPerHour: 40 },
      anodizing: { precision: 0.05, maxSize: 2000, costPerHour: 50 }
    }
  },
  equipment: {
    conventional: {
      millingMachine: { cost: 50000, powerKW: 15, precision: 0.05, maxWorkpiece: 1500 },
      lathe: { cost: 40000, powerKW: 10, precision: 0.05, maxWorkpiece: 1000 },
      drillPress: { cost: 10000, powerKW: 5, precision: 0.1, maxWorkpiece: 500 }
    },
    cnc: {
      cncMill: { cost: 150000, powerKW: 30, precision: 0.005, maxWorkpiece: 2000 },
      cncLathe: { cost: 120000, powerKW: 25, precision: 0.005, maxWorkpiece: 500 }
    },
    additive: {
      metalPrinter: { cost: 500000, powerKW: 50, precision: 0.05, maxWorkpiece: 500 },
      polymerPrinter: { cost: 50000, powerKW: 5, precision: 0.1, maxWorkpiece: 300 }
    },
    robotics: {
      roboticArm: { cost: 80000, powerKW: 10, precision: 0.05, maxWorkpiece: 1000 },
      cobot: { cost: 40000, powerKW: 3, precision: 0.1, maxWorkpiece: 500 }
    },
    inspection: {
      cmm: { cost: 120000, powerKW: 5, precision: 0.002, maxWorkpiece: 1000 },
      visionSystem: { cost: 40000, powerKW: 1, precision: 0.01, maxWorkpiece: 500 }
    }
  },
  skills: {
    cncOperator: { level: 3, certification: 'CNC Programming', validityMonths: 24 },
    welder: { level: 3, certification: 'Certified Welder', validityMonths: 24 },
    assembler: { level: 2, certification: 'Assembly Certification', validityMonths: 36 },
    inspector: { level: 3, certification: 'Quality Inspector', validityMonths: 24 },
    programmer: { level: 4, certification: 'CAM Programming', validityMonths: 12 }
  },
  qualityTemplates: {
    inspection: {
      dimensional: { frequency: 'Every 10 units', tolerance: '±0.01mm', method: 'CMM' },
      visual: { frequency: 'Every unit', tolerance: 'Class A', method: 'Visual Inspection' },
      surface: { frequency: 'Every 50 units', tolerance: 'Ra 0.8', method: 'Surface Profiler' }
    }
  }
};

// ============================================
// PRODUCT CLASSIFICATION ENGINE
// ============================================
class ProductClassificationEngine {
  classify(productDescription) {
    const text = productDescription.toLowerCase();
    const categories = {
      aerospace: ['aircraft', 'aerospace', 'flight', 'wing', 'fuselage', 'engine', 'turbine'],
      automotive: ['automotive', 'car', 'vehicle', 'engine', 'transmission', 'chassis', 'suspension'],
      medical: ['medical', 'surgical', 'implant', 'prosthetic', 'orthopedic', 'dental'],
      consumer: ['consumer', 'electronics', 'appliance', 'furniture', 'toy', 'enclosure'],
      heavy: ['heavy', 'construction', 'mining', 'agriculture', 'truck', 'tractor'],
      energy: ['energy', 'turbine', 'generator', 'wind', 'solar', 'oil', 'gas', 'pump'],
      general: ['bracket', 'mount', 'industrial', 'equipment', 'machinery', 'structural', 'support']
    };

    let scores = {};
    Object.entries(categories).forEach(([category, keywords]) => {
      let score = 0;
      keywords.forEach(keyword => {
        if (text.includes(keyword)) score += 2;
      });
      scores[category] = score;
    });

    const sorted = Object.entries(scores).sort((a, b) => b[1] - a[1]);
    return sorted.length > 0 && sorted[0][1] > 0 ? sorted[0][0] : 'general';
  }
}

// ============================================
// MANUFACTURING STRATEGY GENERATOR
// ============================================
class ManufacturingStrategyGenerator {
  constructor() {
    this.classificationEngine = new ProductClassificationEngine();
  }

  generateStrategy(productDescription, manufacturingType, complexity, quantity) {
    const category = this.classificationEngine.classify(productDescription);
    const strategy = {
      category: category,
      manufacturingType: manufacturingType,
      complexity: complexity,
      estimatedQuantity: quantity,
      recommendedProcesses: [],
      recommendedMaterials: [],
      precision: complexity > 7 ? 0.01 : complexity > 4 ? 0.05 : 0.1,
      quality: category === 'aerospace' ? 'AS9100' : 
               category === 'medical' ? 'ISO 13485' : 
               category === 'automotive' ? 'IATF 16949' : 'ISO 9001',
      tolerance: complexity > 7 ? '±0.005mm' : complexity > 4 ? '±0.05mm' : '±0.1mm'
    };

    const baseMaterials = {
      aerospace: ['titanium', 'aluminum', 'stainless'],
      automotive: ['steel', 'aluminum', 'cast iron'],
      medical: ['titanium', 'stainless', 'polymer'],
      consumer: ['plastic', 'aluminum', 'steel'],
      heavy: ['steel', 'cast iron', 'forged'],
      energy: ['steel', 'stainless', 'alloy'],
      general: ['steel', 'aluminum', 'stainless']
    };

    strategy.recommendedMaterials = baseMaterials[category] || baseMaterials.general;
    strategy.recommendedProcesses = ['CNC Machining', 'Assembly', 'Inspection'];

    return strategy;
  }
}

// ============================================
// BOM GENERATOR
// ============================================
class BOMGenerator {
  generateEBOM(productDescription, strategy, complexity) {
    const components = this.analyzeProduct(productDescription, complexity);
    const ebom = [];

    components.forEach((comp, index) => {
      const item = {
        id: uuidv4(),
        findNumber: String(1000 + index).padStart(4, '0'),
        name: comp.name,
        description: comp.description,
        quantity: comp.quantity || 1,
        uom: comp.uom || 'EA',
        revision: 'A',
        makeBuy: comp.makeBuy || (index === 0 ? 'MAKE' : 'BUY'),
        material: comp.material || strategy.recommendedMaterials[0] || 'Aluminum',
        weight: this.estimateWeight(comp),
        cost: this.estimateCost(comp, strategy),
        level: comp.level || 0,
        parent: comp.parent || null
      };
      ebom.push(item);
    });

    return ebom;
  }

  analyzeProduct(description, complexity) {
    const components = [];
    const isComplex = complexity > 5;
    const productName = this.extractProductName(description);

    components.push({
      name: productName,
      description: `Main ${description.substring(0, 50)}...`,
      quantity: 1,
      uom: 'EA',
      makeBuy: 'MAKE',
      level: 0,
      material: 'Aluminum'
    });

    if (isComplex) {
      components.push({
        name: `${productName}-Frame`,
        description: 'Structural Frame',
        quantity: 1,
        uom: 'EA',
        makeBuy: 'MAKE',
        level: 1,
        parent: 0,
        material: 'Steel'
      });
      components.push({
        name: `${productName}-Housing`,
        description: 'Protective Housing',
        quantity: 1,
        uom: 'EA',
        makeBuy: 'MAKE',
        level: 1,
        parent: 0,
        material: 'Aluminum'
      });
    }

    const count = Math.max(3, Math.min(Math.floor(complexity * 1.5), 8));
    const types = ['Bracket', 'Bolt', 'Nut', 'Washer', 'Screw', 'Pin', 'Bushing', 'Spring', 'MountingPlate', 'Spacer'];
    const materials = ['Steel', 'Aluminum', 'Stainless', 'Titanium', 'Plastic', 'Composite'];

    for (let i = 0; i < count; i++) {
      components.push({
        name: `${types[i % types.length]}-${String(100 + i)}`,
        description: `${types[i % types.length]} for ${productName}`,
        quantity: (i % 3) + 1,
        uom: 'EA',
        makeBuy: i % 2 === 0 ? 'MAKE' : 'BUY',
        level: 2,
        parent: isComplex ? 1 : 0,
        material: materials[i % materials.length]
      });
    }

    return components;
  }

  extractProductName(description) {
    const words = description.split(' ');
    if (words.length > 3) {
      return words.slice(0, 3).join('-').toUpperCase();
    }
    return description.substring(0, 15).toUpperCase().replace(/\s/g, '-');
  }

  estimateWeight(component) {
    const densities = {
      'Steel': 7.85, 'Aluminum': 2.7, 'Titanium': 4.5,
      'Stainless': 8.0, 'Plastic': 1.2, 'Composite': 1.8
    };
    const density = densities[component.material] || 2.7;
    return parseFloat((0.5 * density * (component.quantity || 1)).toFixed(2));
  }

  estimateCost(component, strategy) {
    const costs = {
      'Steel': 2.5, 'Aluminum': 3.2, 'Titanium': 25,
      'Stainless': 6.0, 'Plastic': 3.0, 'Composite': 15
    };
    const cost = costs[component.material] || 3.2;
    const multiplier = 1 + (strategy.complexity || 3) * 0.5;
    return parseFloat((10 * multiplier * cost * (component.quantity || 1)).toFixed(2));
  }
}

// ============================================
// MAIN COMPONENT
// ============================================
const DEManyproduct = ({ onDataGenerated }) => {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [manufacturingType, setManufacturingType] = useState('traditional');
  const [productDescription, setProductDescription] = useState(PRODUCT_TEMPLATES['aluminum-bracket'].description);
  const [selectedTemplate, setSelectedTemplate] = useState('aluminum-bracket');
  const [complexity, setComplexity] = useState(5);
  const [estimatedQuantity, setEstimatedQuantity] = useState(1000);
  const [generatedData, setGeneratedData] = useState(null);
  const [activeStep, setActiveStep] = useState(1);
  const [showAiAssistant, setShowAiAssistant] = useState(false);
  const [aiModel, setAiModel] = useState('offline');
  const [apiKey, setApiKey] = useState('');
  const [validationResults, setValidationResults] = useState(null);
  const [toast, setToast] = useState(null);
  const [isUserTyping, setIsUserTyping] = useState(false);
  const [aiConnectionStatus, setAiConnectionStatus] = useState(null);
  const [aiTestInProgress, setAiTestInProgress] = useState(false);
  const [aiTestResults, setAiTestResults] = useState(null);
  const [aiErrorMessage, setAiErrorMessage] = useState(null);
  const [aiService, setAiService] = useState(null);
  const [aiResponse, setAiResponse] = useState(null);
  const [showAiResponse, setShowAiResponse] = useState(false);

  // ============================================
  // TOAST NOTIFICATION
  // ============================================
  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  }, []);

  // ============================================
  // AI CONNECTION HANDLER
  // ============================================
  const testAIConnection = useCallback(async () => {
    if (!aiModel) {
      showToast('Please select an AI model first.', 'warning');
      return;
    }

    setAiTestInProgress(true);
    setAiTestResults(null);
    setAiErrorMessage(null);

    try {
      const manager = new AIConnectionManager();
      const result = await manager.testConnection(aiModel, apiKey);
      
      setAiTestResults(result);
      setAiConnectionStatus(result);

      if (result.success) {
        // Create AI Service instance
        const service = new AIService(aiModel, apiKey);
        setAiService(service);
        showToast(`✅ Successfully connected to ${AI_MODELS[aiModel]?.name || aiModel}`, 'success');
      } else {
        setAiErrorMessage(result.error);
        showToast(`❌ Connection failed: ${result.error}`, 'error');
      }
    } catch (error) {
      setAiErrorMessage(error.message || 'Unknown connection error');
      showToast(`❌ Connection error: ${error.message}`, 'error');
    } finally {
      setAiTestInProgress(false);
    }
  }, [aiModel, apiKey, showToast]);

  // ============================================
  // HANDLE AI ASSISTANT ACTION
  // ============================================
  const handleAIAssistant = useCallback(async () => {
    if (!productDescription || productDescription.trim().length < 10) {
      showToast('Please provide a product description first.', 'warning');
      return;
    }

    if (!aiService) {
      // Try to connect first
      const manager = new AIConnectionManager();
      const result = await manager.testConnection(aiModel, apiKey);
      
      if (!result.success) {
        setAiErrorMessage(result.error);
        showToast(`❌ Cannot use AI: ${result.error}`, 'error');
        return;
      }
      
      const service = new AIService(aiModel, apiKey);
      setAiService(service);
    }

    setLoading(true);
    setProgress(0);
    setShowAiResponse(false);
    setAiResponse(null);

    try {
      setProgress(30);
      const analysisResult = await aiService.analyzeProduct(productDescription);
      
      if (analysisResult.success) {
        setAiResponse(analysisResult);
        setShowAiResponse(true);
        
        // Update fields based on AI analysis
        if (analysisResult.analysis) {
          const { complexity: aiComplexity, estimatedMaterials, recommendedProcesses } = analysisResult.analysis;
          if (aiComplexity) setComplexity(Math.min(10, Math.max(1, Math.round(aiComplexity))));
        }
        
        showToast('✅ AI analysis completed successfully!', 'success');
      } else {
        setAiErrorMessage(analysisResult.error || 'AI analysis failed');
        showToast(`❌ AI analysis failed: ${analysisResult.error}`, 'error');
      }
    } catch (error) {
      setAiErrorMessage(error.message || 'AI service error');
      showToast(`❌ AI error: ${error.message}`, 'error');
    } finally {
      setLoading(false);
      setProgress(100);
    }
  }, [productDescription, aiService, aiModel, apiKey, showToast]);

  // ============================================
  // HANDLE TEMPLATE CHANGE
  // ============================================
  const handleTemplateChange = useCallback((templateKey) => {
    setSelectedTemplate(templateKey);
    const template = PRODUCT_TEMPLATES[templateKey];
    if (template) {
      setProductDescription(template.description);
      setComplexity(template.complexity);
      setEstimatedQuantity(template.quantity);
      setIsUserTyping(false);
      showToast(`✅ Loaded template: ${template.name}`, 'success');
    }
  }, [showToast]);

  // ============================================
  // HANDLE DESCRIPTION CHANGE
  // ============================================
  const handleDescriptionChange = useCallback((e) => {
    const value = e.target.value;
    setProductDescription(value);
    setIsUserTyping(true);
    
    let matched = false;
    Object.entries(PRODUCT_TEMPLATES).forEach(([key, template]) => {
      if (value === template.description) {
        setSelectedTemplate(key);
        setComplexity(template.complexity);
        setEstimatedQuantity(template.quantity);
        setIsUserTyping(false);
        matched = true;
      }
    });
    
    if (!matched && value.trim()) {
      setSelectedTemplate('custom');
    }
  }, []);

  // ============================================
  // PRODUCT ANALYSIS
  // ============================================
  const analyzeProduct = useCallback(async () => {
    if (!productDescription || productDescription.trim().length < 10) {
      showToast('Please provide a detailed product description (minimum 10 characters).', 'warning');
      return;
    }

    setLoading(true);
    setProgress(0);
    setShowResults(false);
    
    try {
      setActiveStep(1);
      setProgress(20);
      
      const strategy = new ManufacturingStrategyGenerator();
      const productStrategy = strategy.generateStrategy(
        productDescription,
        manufacturingType,
        complexity,
        estimatedQuantity
      );

      setActiveStep(2);
      setProgress(40);
      
      const artifacts = await generateArtifacts(
        productDescription,
        productStrategy,
        manufacturingType
      );

      setActiveStep(3);
      setProgress(70);
      
      const validation = await validateArtifacts(artifacts, productStrategy);
      setValidationResults(validation);

      setActiveStep(4);
      setProgress(90);
      
      const completeData = buildCompleteDataStructure(artifacts, productStrategy);
      setGeneratedData(completeData);
      
      setProgress(100);
      setShowResults(true);
      
      showToast('✅ Manufacturing artifacts generated successfully!');
      
      if (onDataGenerated) {
        onDataGenerated(completeData);
      }
      
    } catch (error) {
      console.error('Product generation error:', error);
      showToast(`❌ Error: ${error.message || 'Failed to generate artifacts'}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [productDescription, manufacturingType, complexity, estimatedQuantity, onDataGenerated, showToast]);

  // ============================================
  // ARTIFACT GENERATION
  // ============================================
  const generateArtifacts = async (description, strategy, type) => {
    const bomGenerator = new BOMGenerator();
    
    const ebom = bomGenerator.generateEBOM(description, strategy, complexity);
    
    const mbom = ebom.map(item => ({
      ...item,
      id: uuidv4(),
      name: `${item.name}-M`,
      operationReference: `OP-${String(item.findNumber).padStart(4, '0')}`,
      description: `Manufacturing: ${item.description}`
    }));

    const bop = generateBOP(description, strategy, ebom.length, type);
    const equipment = generateEquipment(strategy, type);
    const tools = generateTools(strategy);
    const skills = generateSkills(strategy);
    const qualityPlans = generateQualityPlans(description, strategy);
    const ewi = generateEWI(description, strategy);

    return {
      ebom,
      mbom,
      bop,
      equipment,
      tools,
      skills,
      qualityPlans,
      ewi
    };
  };

  // ============================================
  // BOP GENERATION
  // ============================================
  const generateBOP = (description, strategy, componentCount, manufacturingTypeParam) => {
    const numOps = Math.max(5, Math.min(20, componentCount * 2));
    const operations = [];
    const workCenters = ['WC-CNC-01', 'WC-ASM-01', 'WC-INSP-01', 'WC-FINISH-01', 'WC-MAINT-01'];
    
    const processTypes = {
      traditional: ['Cutting', 'Drilling', 'Turning', 'Milling', 'Grinding', 'Welding', 'Assembly', 'Inspection', 'Finishing'],
      autonomous: ['Additive', 'RoboticAssembly', 'AIInspection', 'AutomatedFinishing', 'AutonomousMilling', 'SmartWelding']
    };

    const processes = manufacturingTypeParam === 'traditional' ? processTypes.traditional : processTypes.autonomous;

    for (let i = 0; i < numOps; i++) {
      const opSequence = String((i + 1) * 10).padStart(4, '0');
      const process = processes[i % processes.length];
      const wc = workCenters[i % workCenters.length];
      
      operations.push({
        id: uuidv4(),
        sequence: opSequence,
        name: `${process} OP-${opSequence}`,
        description: `${process} operation for ${description.substring(0, 30)}...`,
        workCenter: wc,
        standardTime: parseFloat((5 + Math.random() * 20).toFixed(1)),
        setupTime: parseFloat((1 + Math.random() * 5).toFixed(1)),
        tools: i % 2 === 0 ? `T-${String(100 + i)}` : `T-${String(200 + i)}`,
        skills: i % 3 === 0 ? 'CNC-5AXIS' : i % 3 === 1 ? 'Assembly-LEVEL2' : 'Quality-Inspector',
        controlKey: `PC-${String(i + 1).padStart(3, '0')}`,
        consumes: i % 2 === 0 ? `RAW-${String(i + 1).padStart(3, '0')}` : '',
        byproducts: i % 3 === 0 ? `SCRAP-${String(i + 1)}` : '',
        parameter: `TEMP-${150 + i * 10}`,
        inspection: i % 4 === 0 ? 'Visual + Dimensional' : 'Visual',
        output: `OUT-${String(i + 1)}`,
        queTime: parseFloat((0.5 + Math.random() * 3).toFixed(1)),
        machine: `MACHINE-${String(i + 1)}`,
        tooling: `TOOLING-${String(i + 1)}`,
        sourceLocation: `LOC-${String(i + 1)}`,
        moveTime: parseFloat((0.5 + Math.random() * 2).toFixed(1)),
        operationType: process,
        category: 'Manufacturing',
        subCategory: processTypes.traditional.includes(process) ? 'Traditional' : 'Autonomous'
      });
    }

    const groups = [];
    const groupSize = Math.max(3, Math.floor(numOps / 3));
    
    for (let i = 0; i < operations.length; i += groupSize) {
      const group = operations.slice(i, i + groupSize);
      groups.push({
        name: `FG-${String(Math.floor(i / groupSize) + 1).padStart(3, '0')}`,
        description: `Functional Group ${Math.floor(i / groupSize) + 1}`,
        sequence: String((Math.floor(i / groupSize) + 1) * 10).padStart(4, '0'),
        operations: group
      });
    }

    return {
      productBOP: {
        name: `BOP-${description.substring(0, 20).toUpperCase().replace(/\s/g, '-')}`,
        description: `Bill of Process for ${description.substring(0, 50)}...`,
        revision: 'A',
        status: 'Draft',
        processes: [{
          name: 'Main Process',
          description: 'Primary manufacturing process flow',
          type: 'ProcessBOP',
          sequence: '0010',
          plantBOPs: [{
            name: 'Primary Plant BOP',
            plant: 'JEDDAH',
            description: 'Main production facility',
            functionalGroups: groups
          }]
        }]
      }
    };
  };

  // ============================================
  // EQUIPMENT GENERATION
  // ============================================
  const generateEquipment = (strategy, type) => {
    const equipment = [];
    const equipmentDB = ManufacturingKnowledgeBase.equipment;
    
    let categories = [];
    if (type === 'autonomous') {
      categories = ['cnc', 'additive', 'robotics', 'inspection'];
    } else {
      categories = ['conventional', 'cnc', 'inspection'];
    }

    categories.forEach(category => {
      const items = equipmentDB[category] || {};
      Object.entries(items).forEach(([name, specs]) => {
        if (Math.random() > 0.3) {
          equipment.push({
            id: uuidv4(),
            name: name.toUpperCase(),
            level: Math.random() > 0.5 ? 'WorkUnit' : 'ProductionLine',
            description: `${name} for ${strategy.category} manufacturing`,
            parent: '',
            technology: getTechnologyFromEquipment(name),
            costRate: parseFloat((specs.cost / 1000).toFixed(0)),
            capacity: parseFloat((specs.maxWorkpiece / 100).toFixed(0)),
            bmideType: `MES${category.charAt(0).toUpperCase() + category.slice(1)}`
          });
        }
      });
    });

    return equipment;
  };

  // ============================================
  // HELPER FUNCTIONS
  // ============================================
  const getTechnologyFromEquipment = (name) => {
    if (name.includes('CNC')) return 'CNC';
    if (name.includes('Printer') || name.includes('Additive')) return '3D Printing';
    if (name.includes('Robot') || name.includes('Cobot') || name.includes('AGV')) return 'Robotics';
    if (name.includes('CMM') || name.includes('Scanner') || name.includes('Vision')) return 'Inspection';
    return 'Conventional';
  };

  const generateTools = (strategy) => {
    const tools = [];
    const toolTypes = ['Fixture', 'Cutting Tool', 'Measuring Device', 'Tooling', 'Consumable'];
    const toolNames = ['T-001', 'T-002', 'T-003', 'T-004', 'T-005', 'T-006', 'T-007', 'T-008'];
    
    for (let i = 0; i < 8; i++) {
      tools.push({
        id: uuidv4(),
        name: toolNames[i % toolNames.length],
        type: toolTypes[i % toolTypes.length],
        description: `${toolTypes[i % toolTypes.length]} for ${strategy.category} production`,
        quantity: i % 3 === 0 ? 2 : 1,
        calibrationDue: i % 2 === 0 ? '2025-12-31' : '2026-06-30',
        manufacturer: ['Sandvik', 'Kennametal', 'Mitsubishi', 'ISCAR'][i % 4],
        cost: parseFloat((100 + Math.random() * 900).toFixed(0))
      });
    }
    return tools;
  };

  const generateSkills = (strategy) => {
    const skills = [];
    const skillDB = ManufacturingKnowledgeBase.skills;
    
    Object.entries(skillDB).forEach(([name, data]) => {
      if (Math.random() > 0.3) {
        skills.push({
          id: uuidv4(),
          name: name.charAt(0).toUpperCase() + name.slice(1).replace(/([A-Z])/g, ' $1'),
          description: `${name} certification for ${strategy.category} manufacturing`,
          level: data.level,
          validityMonths: data.validityMonths,
          certificationBody: data.certification,
          requiresRecert: Math.random() > 0.5
        });
      }
    });
    return skills;
  };

  const generateQualityPlans = (description, strategy) => {
    const plans = [];
    const characteristics = [
      { name: 'Dimensional Accuracy', description: 'Critical dimensions verification', tolerance: '±0.01mm', method: 'CMM', frequency: 'Every 10 units' },
      { name: 'Surface Finish', description: 'Surface roughness inspection', tolerance: 'Ra 0.8', method: 'Surface Profiler', frequency: 'Every 50 units' },
      { name: 'Material Hardness', description: 'Hardness testing', tolerance: 'HRC 50-55', method: 'Rockwell Test', frequency: 'Every 100 units' },
      { name: 'Visual Inspection', description: 'Surface defects check', tolerance: 'Class A', method: 'Visual', frequency: 'Every unit' }
    ];

    for (let i = 0; i < 3; i++) {
      plans.push({
        name: `QP-${String(i + 1).padStart(3, '0')}`,
        description: `Quality Plan for ${description.substring(0, 30)}... - Phase ${i + 1}`,
        targetOperation: `OP-${String((i + 1) * 10).padStart(4, '0')}`,
        inspectionFrequency: i === 0 ? 'Every unit' : i === 1 ? 'Every 10 units' : 'Every 50 units',
        characteristics: characteristics.slice(i * 2, i * 2 + 2)
      });
    }
    return plans;
  };

  const generateEWI = (description, strategy) => {
    const ewi = [];
    const types = ['PDF', 'WORD', 'PPT', 'VIDEO', '3D'];
    const authors = ['John Smith', 'Jane Doe', 'Robert Johnson', 'Mary Williams'];
    
    for (let i = 0; i < 4; i++) {
      ewi.push({
        id: `EWI-${String(i + 1).padStart(3, '0')}`,
        name: `Work_Instruction_${String(i + 1)}`,
        type: types[i % types.length],
        description: `Electronic Work Instruction for operation ${String((i + 1) * 10).padStart(4, '0')}`,
        targetOperation: `OP-${String((i + 1) * 10).padStart(4, '0')}`,
        revision: 'A',
        author: authors[i % authors.length],
        approvalDate: new Date().toISOString().split('T')[0]
      });
    }
    return ewi;
  };

  // ============================================
  // VALIDATION FUNCTIONS
  // ============================================
  const validateArtifacts = async (artifacts, strategy) => {
    const results = {
      valid: true,
      issues: [],
      warnings: [],
      suggestions: []
    };

    if (!artifacts.ebom || artifacts.ebom.length === 0) {
      results.valid = false;
      results.issues.push('EBOM is empty');
    }

    if (!artifacts.mbom || artifacts.mbom.length === 0) {
      results.valid = false;
      results.issues.push('MBOM is empty');
    }

    if (!artifacts.bop || !artifacts.bop.productBOP) {
      results.valid = false;
      results.issues.push('BOP is missing');
    }

    if (!artifacts.equipment || artifacts.equipment.length === 0) {
      results.warnings.push('No equipment defined');
    }

    if (!artifacts.tools || artifacts.tools.length === 0) {
      results.warnings.push('No tools defined');
    }

    if (!artifacts.skills || artifacts.skills.length === 0) {
      results.warnings.push('No skills defined');
    }

    return results;
  };

  // ============================================
  // DATA STRUCTURE BUILDER
  // ============================================
  const buildCompleteDataStructure = (artifacts, strategy) => {
    const productName = artifacts.ebom[0]?.name || 'DEFAULT-PRODUCT';
    
    return {
      product: {
        name: `PRODUCT-${productName}`,
        description: productDescription,
        revision: 'A',
        mbomName: `${productName}-M`,
        mbomRev: '000',
        bopName: `BOP-${productName}`,
        classification: strategy.category,
        manufacturingType: manufacturingType,
        complexity: complexity,
        estimatedQuantity: estimatedQuantity
      },
      workPackage: {
        id: `WP-${Date.now()}`,
        name: `Work Package ${productName}`,
        plant: 'JEDDAH',
        owner: 'System Generated',
        estimatedStart: new Date().toISOString(),
        estimatedEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
      },
      includes: {
        ebom: true,
        mbom: true,
        bop: true,
        boe: true,
        workPackage: true,
        tools: true,
        skills: true,
        ewi: true,
        quality: true
      },
      ebom: artifacts.ebom,
      mbom: artifacts.mbom,
      bopData: artifacts.bop,
      equip: artifacts.equipment,
      tools: artifacts.tools,
      skills: artifacts.skills,
      qualityPlans: artifacts.qualityPlans,
      ewiDocs: artifacts.ewi,
      bt: {
        ebomItem: 'Part',
        ebomRevision: 'Part Revision',
        mbomItem: 'ARA4MPart',
        mbomRevision: 'ARA4MPartRevision',
        bopItem: 'MEProductBOP',
        bopRevision: 'MEProductBOPRevision',
        operation: 'MEOP',
        operationRevision: 'MEOPRevision',
        funcGroup: 'Mbc0FuncGroup',
        funcGroupRevision: 'Mbc0FuncGroupRevision',
        workPackage: 'MECollaborationContext',
        tools: 'MESResource',
        skill: 'MESSkill',
        document: 'Document'
      },
      strategy: strategy,
      validation: validationResults,
      aiModel: aiModel,
      aiConnectionStatus: aiConnectionStatus
    };
  };

  // ============================================
  // EXPORT FUNCTIONS
  // ============================================
  const exportData = useCallback(async () => {
    if (!generatedData) return;

    try {
      const zip = new JSZip();
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const baseName = generatedData.product.name.replace(/[^a-zA-Z0-9]/g, '_');

      const dataFolder = zip.folder(`${baseName}_Data`);

      // Export EBOM
      dataFolder.file('EBOM.csv', Papa.unparse(generatedData.ebom.map(item => ({
        'Find Number': item.findNumber,
        'Component Name': item.name,
        Description: item.description,
        Quantity: item.quantity,
        UOM: item.uom,
        Revision: item.revision,
        'Make/Buy': item.makeBuy,
        Material: item.material,
        'Weight (KG)': item.weight,
        Cost: item.cost
      }))));

      // Export MBOM
      dataFolder.file('MBOM.csv', Papa.unparse(generatedData.mbom.map(item => ({
        'Find Number': item.findNumber,
        'Component Name': item.name,
        Description: item.description,
        Quantity: item.quantity,
        UOM: item.uom,
        Revision: item.revision,
        'Make/Buy': item.makeBuy,
        'Operation Ref': item.operationReference,
        Material: item.material,
        'Weight (KG)': item.weight
      }))));

      // Export BOP
      const allOps = extractAllOperations(generatedData.bopData);
      if (allOps.length > 0) {
        dataFolder.file('BOP.csv', Papa.unparse(allOps.map(op => ({
          Sequence: op.sequence,
          'Operation Name': op.name,
          Description: op.description,
          'Work Center': op.workCenter,
          'Std Time (min)': op.standardTime,
          'Setup Time (min)': op.setupTime,
          Tools: op.tools,
          Skills: op.skills,
          'Control Key': op.controlKey,
          Consumes: op.consumes,
          Byproducts: op.byproducts,
          Parameter: op.parameter,
          Inspection: op.inspection,
          Output: op.output,
          'Queue Time': op.queTime,
          'Functional Group': op.functionalGroup,
          'Plant': op.plant,
          'Process': op.process,
          'Machine': op.machine,
          'Tooling': op.tooling,
          'Source Location': op.sourceLocation,
          'Move Time': op.moveTime
        }))));
      }

      // Export Equipment
      dataFolder.file('BOE.csv', Papa.unparse(generatedData.equip.map(eq => ({
        Name: eq.name,
        Level: eq.level,
        Description: eq.description,
        Parent: eq.parent,
        Technology: eq.technology,
        'Cost Rate ($/h)': eq.costRate,
        'Capacity (hrs)': eq.capacity,
        'BMIDE Type': eq.bmideType
      }))));

      // Export Tools
      dataFolder.file('Tools.csv', Papa.unparse(generatedData.tools.map(t => ({
        Name: t.name,
        Type: t.type,
        Description: t.description,
        Quantity: t.quantity,
        'Calibration Due': t.calibrationDue,
        Manufacturer: t.manufacturer,
        Cost: t.cost
      }))));

      // Export Skills
      dataFolder.file('Skills.csv', Papa.unparse(generatedData.skills.map(s => ({
        Name: s.name,
        Description: s.description,
        Level: s.level,
        'Validity (months)': s.validityMonths,
        'Certification Body': s.certificationBody,
        'Requires Recert': s.requiresRecert
      }))));

      // Export Quality Plans
      dataFolder.file('QualityPlans.csv', Papa.unparse(generatedData.qualityPlans.map(qp => ({
        'Plan Name': qp.name,
        Description: qp.description,
        'Target Operation': qp.targetOperation,
        'Inspection Frequency': qp.inspectionFrequency,
        Characteristics: (qp.characteristics || []).map(c => c.name).join('; ')
      }))));

      // Export EWI
      dataFolder.file('EWI.csv', Papa.unparse(generatedData.ewiDocs.map(ewi => ({
        'Document ID': ewi.id,
        Name: ewi.name,
        Type: ewi.type,
        Description: ewi.description,
        'Target Operation': ewi.targetOperation,
        Revision: ewi.revision,
        Author: ewi.author,
        'Approval Date': ewi.approvalDate
      }))));

      // Export Summary with AI info
      dataFolder.file('summary.json', JSON.stringify({
        product: generatedData.product,
        strategy: generatedData.strategy,
        validation: generatedData.validation,
        template: selectedTemplate,
        isUserTyping: isUserTyping,
        aiModel: aiModel,
        aiConnected: aiConnectionStatus?.success || false,
        counts: {
          ebom: generatedData.ebom.length,
          mbom: generatedData.mbom.length,
          operations: allOps.length,
          equipment: generatedData.equip.length,
          tools: generatedData.tools.length,
          skills: generatedData.skills.length,
          qualityPlans: generatedData.qualityPlans.length,
          ewiDocs: generatedData.ewiDocs.length
        },
        generatedDate: new Date().toISOString()
      }, null, 2));

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      saveAs(zipBlob, `${baseName}_Manufacturing_Artifacts_${timestamp}.zip`);
      
      showToast('✅ ZIP file exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      showToast('❌ Failed to export ZIP file', 'error');
    }
  }, [generatedData, selectedTemplate, isUserTyping, aiModel, aiConnectionStatus, showToast]);

  // ============================================
  // RENDER AI ASSISTANT MODAL
  // ============================================
  const renderAIAssistantModal = () => {
    const selectedModelConfig = AI_MODELS[aiModel] || AI_MODELS['offline'];
    const status = aiConnectionStatus;

    return (
      <div style={S.modal}>
        <div style={{ ...S.modalC, maxWidth: '700px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Cpu size={20} color={T.pl} />
              <span style={{ fontSize: '1.1rem', fontWeight: 700 }}>AI Manufacturing Assistant</span>
              {status && (
                <span style={{ 
                  ...S.badge(status.success ? T.g : T.d),
                  marginLeft: '8px'
                }}>
                  {status.success ? '🟢 Connected' : '🔴 Disconnected'}
                </span>
              )}
            </div>
            <button onClick={() => setShowAiAssistant(false)} style={{ ...S.btn, ...S.bGho }}>
              <X size={20} />
            </button>
          </div>

          {/* AI Model Selection */}
          <div style={{ marginBottom: '12px' }}>
            <div style={S.label}>Select AI Model</div>
            <select
              style={S.select}
              value={aiModel}
              onChange={(e) => {
                setAiModel(e.target.value);
                setAiConnectionStatus(null);
                setAiTestResults(null);
                setAiErrorMessage(null);
                if (!AI_MODELS[e.target.value]?.requiresApiKey) {
                  setApiKey('');
                }
              }}
            >
              {Object.entries(AI_MODELS).map(([key, config]) => (
                <option key={key} value={key}>
                  {config.name} - {config.provider}
                  {config.requiresApiKey ? ' (API Key Required)' : ' (No API Key)'}
                </option>
              ))}
            </select>
          </div>

          {/* Model Info */}
          <div style={{ 
            padding: '10px 14px', 
            background: T.s, 
            borderRadius: '8px', 
            marginBottom: '12px',
            border: `1px solid ${T.b}`
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.85rem', color: T.t }}>
                  {selectedModelConfig.name}
                </div>
                <div style={{ fontSize: '0.75rem', color: T.t2 }}>
                  {selectedModelConfig.description}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '0.6rem', color: T.t3 }}>
                  {selectedModelConfig.requiresApiKey ? '🔑 API Key Required' : '🌐 No API Key Needed'}
                </div>
                {selectedModelConfig.costPer1000Tokens > 0 && (
                  <div style={{ fontSize: '0.6rem', color: T.t3 }}>
                    💰 ${selectedModelConfig.costPer1000Tokens}/1K tokens
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '4px', marginTop: '6px', flexWrap: 'wrap' }}>
              {selectedModelConfig.features.map((feature, i) => (
                <span key={i} style={{ ...S.badge(T.p), fontSize: '0.55rem' }}>
                  {feature}
                </span>
              ))}
            </div>
          </div>

          {/* API Key Input */}
          {selectedModelConfig.requiresApiKey && (
            <div style={{ marginBottom: '12px' }}>
              <div style={S.label}>API Key</div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="password"
                  style={{ ...S.input, flex: 1 }}
                  placeholder={selectedModelConfig.apiKeyPlaceholder}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                />
                <button
                  style={{ ...S.btn, ...(aiConnectionStatus?.success ? S.bSuc : S.bPri) }}
                  onClick={testAIConnection}
                  disabled={aiTestInProgress}
                >
                  {aiTestInProgress ? (
                    <>
                      <RefreshCw size={14} className="spin" />
                      Testing...
                    </>
                  ) : (
                    <>
                      <Link size={14} />
                      Test Connection
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Connection Status / Error Display */}
          {aiErrorMessage && (
            <div style={{
              padding: '10px 14px',
              background: `${T.d}15`,
              border: `1px solid ${T.d}`,
              borderRadius: '6px',
              marginBottom: '12px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: T.d }}>
                <AlertTriangle size={16} />
                <span style={{ fontWeight: 600, fontSize: '0.8rem' }}>Connection Error</span>
              </div>
              <div style={{ fontSize: '0.75rem', color: T.t2, marginTop: '4px' }}>
                {aiErrorMessage}
              </div>
              <div style={{ fontSize: '0.65rem', color: T.t3, marginTop: '4px' }}>
                💡 {selectedModelConfig.requiresApiKey 
                  ? 'Check your API key format and try again. Make sure you have credits in your account.' 
                  : 'This model may not be available offline. Try selecting a different model.'}
              </div>
            </div>
          )}

          {/* Test Results Display */}
          {aiTestResults && (
            <div style={{
              padding: '10px 14px',
              background: aiTestResults.success ? `${T.g}10` : `${T.d}10`,
              border: `1px solid ${aiTestResults.success ? T.g : T.d}`,
              borderRadius: '6px',
              marginBottom: '12px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: aiTestResults.success ? T.g : T.d }}>
                {aiTestResults.success ? <CheckCircle2 size={16} /> : <X size={16} />}
                <span style={{ fontWeight: 600, fontSize: '0.8rem' }}>
                  {aiTestResults.success ? 'Connection Successful' : 'Connection Failed'}
                </span>
              </div>
              <div style={{ fontSize: '0.7rem', color: T.t2, marginTop: '4px' }}>
                {aiTestResults.success ? (
                  <>
                    ✅ Connected to {selectedModelConfig.name}
                    <span style={{ marginLeft: '8px' }}>⏱️ {aiTestResults.latency}ms</span>
                    <span style={{ marginLeft: '8px' }}>📅 {new Date(aiTestResults.timestamp).toLocaleTimeString()}</span>
                  </>
                ) : (
                  <>
                    ❌ {aiTestResults.error}
                  </>
                )}
              </div>
            </div>
          )}

          {/* AI Response Display */}
          {showAiResponse && aiResponse && (
            <div style={{
              padding: '10px 14px',
              background: `${T.g}10`,
              border: `1px solid ${T.g}`,
              borderRadius: '6px',
              marginBottom: '12px',
              maxHeight: '200px',
              overflowY: 'auto'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: T.g }}>
                <Sparkles size={16} />
                <span style={{ fontWeight: 600, fontSize: '0.8rem' }}>AI Analysis Results</span>
                <span style={{ fontSize: '0.6rem', color: T.t3, marginLeft: 'auto' }}>
                  Model: {aiResponse.model || aiModel}
                </span>
              </div>
              <div style={{ fontSize: '0.75rem', color: T.t2, marginTop: '4px' }}>
                {aiResponse.analysis && (
                  <div>
                    <div>📊 Product Type: {aiResponse.analysis.productType}</div>
                    <div>🏭 Industry: {aiResponse.analysis.industry}</div>
                    <div>📈 Complexity: {aiResponse.analysis.complexity}/10</div>
                    <div>🧪 Materials: {aiResponse.analysis.estimatedMaterials?.join(', ')}</div>
                    <div>⚙️ Processes: {aiResponse.analysis.recommendedProcesses?.join(', ')}</div>
                  </div>
                )}
                {aiResponse.fallbackAnalysis && (
                  <div style={{ color: T.w }}>
                    ⚠️ Using fallback analysis (offline mode)
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              style={{ ...S.btn, ...S.bPri }}
              onClick={handleAIAssistant}
              disabled={loading || !productDescription.trim()}
            >
              {loading ? (
                <>
                  <RefreshCw size={16} className="spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Sparkles size={16} />
                  Analyze Product with AI
                </>
              )}
            </button>
            <button
              style={{ ...S.btn, ...S.bGho }}
              onClick={() => {
                setShowAiAssistant(false);
                if (productDescription) {
                  analyzeProduct();
                }
              }}
            >
              <Wand2 size={16} />
              Generate Artifacts
            </button>
            <button
              style={{ ...S.btn, ...S.bGho }}
              onClick={() => setShowAiAssistant(false)}
            >
              <X size={16} />
              Close
            </button>
          </div>

          {/* Helper Text */}
          <div style={{ 
            fontSize: '0.65rem', 
            color: T.t3, 
            marginTop: '12px',
            padding: '8px 12px',
            background: T.s,
            borderRadius: '4px'
          }}>
            💡 <strong>Tip:</strong> {aiConnectionStatus?.success 
              ? 'AI is ready! Click "Analyze Product" to get intelligent recommendations.' 
              : 'Connect to an AI model first for enhanced analysis. Offline mode is always available.'}
          </div>
        </div>
      </div>
    );
  };

  // ============================================
  // RENDER FUNCTIONS
  // ============================================
  const renderStepIndicator = () => {
    const steps = ['Product Analysis', 'Artifact Generation', 'Validation', 'Finalization'];
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {steps.map((step, index) => (
          <React.Fragment key={index}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 12px',
              borderRadius: '20px',
              background: activeStep === index + 1 ? T.p : (activeStep > index + 1 ? T.g : T.b),
              color: activeStep === index + 1 || activeStep > index + 1 ? '#fff' : T.t3,
              fontSize: '0.75rem',
              fontWeight: 600
            }}>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                background: activeStep === index + 1 ? 'rgba(255,255,255,0.2)' : 'transparent',
                fontSize: '0.65rem'
              }}>
                {activeStep > index + 1 ? '✓' : index + 1}
              </span>
              {step}
            </div>
            {index < steps.length - 1 && (
              <div style={{
                width: '20px',
                height: '2px',
                background: activeStep > index + 1 ? T.g : T.b
              }} />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderProductInput = () => {
    return (
      <div style={S.card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <Sparkles size={24} color={T.pl} />
          <div>
            <div style={S.header}>Product Specification</div>
            <div style={S.subHeader}>Select a product template or describe your product in detail</div>
          </div>
        </div>

        {/* Product Template Dropdown */}
        <div style={{ marginBottom: '16px' }}>
          <div style={S.label}>Product Template</div>
          <select
            style={S.select}
            value={selectedTemplate}
            onChange={e => handleTemplateChange(e.target.value)}
          >
            <option value="" disabled>Select a product template...</option>
            {Object.entries(PRODUCT_TEMPLATES).map(([key, template]) => (
              <option key={key} value={key}>
                {template.name} - {template.industry}
              </option>
            ))}
            <option value="custom">✏️ Custom Product (User Defined)</option>
          </select>
          {selectedTemplate !== 'custom' && selectedTemplate && (
            <div style={{ fontSize: '0.7rem', color: T.t2, marginTop: '4px' }}>
              ℹ️ {PRODUCT_TEMPLATES[selectedTemplate]?.industry} | 
              Complexity: {PRODUCT_TEMPLATES[selectedTemplate]?.complexity}/10 | 
              Volume: {PRODUCT_TEMPLATES[selectedTemplate]?.quantity}/year
            </div>
          )}
        </div>

        <div style={{ ...S.grid(1), marginBottom: '16px' }}>
          <div>
            <div style={S.label}>
              Product Description
              {isUserTyping && <span style={{ color: T.w, marginLeft: '8px' }}>✏️ Custom</span>}
              {!isUserTyping && selectedTemplate && selectedTemplate !== 'custom' && (
                <span style={{ color: T.g, marginLeft: '8px' }}>📋 Template</span>
              )}
            </div>
            <textarea
              style={S.textarea}
              placeholder="Describe your product in detail..."
              value={productDescription}
              onChange={handleDescriptionChange}
            />
          </div>
        </div>

        <div style={S.grid(3)}>
          <div>
            <div style={S.label}>Manufacturing Approach</div>
            <select
              style={S.select}
              value={manufacturingType}
              onChange={e => setManufacturingType(e.target.value)}
            >
              <option value="traditional">Traditional Manufacturing</option>
              <option value="autonomous">Autonomous Manufacturing</option>
            </select>
          </div>
          <div>
            <div style={S.label}>Complexity Level (1-10)</div>
            <input
              type="range"
              min="1"
              max="10"
              step="1"
              value={complexity}
              onChange={e => setComplexity(parseInt(e.target.value))}
              style={{ width: '100%' }}
            />
            <div style={{ textAlign: 'center', fontSize: '0.8rem', color: T.t2 }}>
              Level: {complexity} {complexity <= 3 ? '(Simple)' : complexity <= 7 ? '(Medium)' : '(Complex)'}
            </div>
          </div>
          <div>
            <div style={S.label}>Estimated Quantity</div>
            <input
              type="number"
              style={S.input}
              value={estimatedQuantity}
              onChange={e => setEstimatedQuantity(parseInt(e.target.value) || 0)}
              min="1"
              step="1"
            />
          </div>
        </div>

        <div style={{ marginTop: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            style={{ ...S.btn, ...S.bPri }}
            onClick={analyzeProduct}
            disabled={loading || !productDescription.trim()}
          >
            {loading ? (
              <>
                <RefreshCw size={16} className="spin" />
                Generating...
              </>
            ) : (
              <>
                <Wand2 size={16} />
                Generate Manufacturing Artifacts
              </>
            )}
          </button>
          <button
            style={{ ...S.btn, ...S.bPP }}
            onClick={() => setShowAiAssistant(true)}
          >
            <Cpu size={16} />
            AI Assistant
            {aiConnectionStatus?.success && <span style={{ ...S.badge(T.g), marginLeft: '4px' }}>Active</span>}
            {aiConnectionStatus && !aiConnectionStatus.success && <span style={{ ...S.badge(T.d), marginLeft: '4px' }}>Error</span>}
          </button>
          {isUserTyping && (
            <button
              style={{ ...S.btn, ...S.bWar }}
              onClick={() => {
                setSelectedTemplate('aluminum-bracket');
                const template = PRODUCT_TEMPLATES['aluminum-bracket'];
                setProductDescription(template.description);
                setComplexity(template.complexity);
                setEstimatedQuantity(template.quantity);
                setIsUserTyping(false);
                showToast('✅ Reset to default template', 'success');
              }}
            >
              <RotateCcw size={14} />
              Reset to Template
            </button>
          )}
        </div>

        {loading && (
          <div style={{ marginTop: '12px' }}>
            <div style={{ ...S.progressBar }}>
              <div style={{ ...S.progressFill, width: `${progress}%`, background: T.p }} />
            </div>
            <div style={{ marginTop: '4px', fontSize: '0.75rem', color: T.t2 }}>
              {activeStep <= 1 && 'Analyzing product specifications...'}
              {activeStep === 2 && 'Generating manufacturing artifacts...'}
              {activeStep === 3 && 'Validating data...'}
              {activeStep === 4 && 'Finalizing...'}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderResults = () => {
    if (!showResults || !generatedData) return null;

    return (
      <div style={S.card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <CheckCircle2 size={24} color={T.g} />
          <div>
            <div style={S.header}>✅ Manufacturing Artifacts Generated</div>
            <div style={S.subHeader}>
              Complete set of manufacturing artifacts ready for PLMXML export
              {selectedTemplate && selectedTemplate !== 'custom' && (
                <span style={{ marginLeft: '8px', ...S.badge(T.p) }}>
                  📋 {PRODUCT_TEMPLATES[selectedTemplate]?.name}
                </span>
              )}
              {isUserTyping && (
                <span style={{ marginLeft: '8px', ...S.badge(T.w) }}>
                  ✏️ Custom Product
                </span>
              )}
              {aiConnectionStatus?.success && (
                <span style={{ marginLeft: '8px', ...S.badge(T.g) }}>
                  🤖 AI Enhanced
                </span>
              )}
            </div>
          </div>
        </div>

        <div style={S.grid(4)}>
          {[
            { label: 'EBOM Items', value: generatedData.ebom.length, color: T.cy },
            { label: 'MBOM Items', value: generatedData.mbom.length, color: T.pk },
            { label: 'Operations', value: extractAllOperations(generatedData.bopData).length, color: T.w },
            { label: 'Equipment', value: generatedData.equip.length, color: T.pp }
          ].map((stat, i) => (
            <div key={i} style={S.statCard}>
              <div style={{ ...S.statVal, color: stat.color }}>{stat.value}</div>
              <div style={S.statLbl}>{stat.label}</div>
            </div>
          ))}
        </div>

        {validationResults && (
          <div style={{ margin: '12px 0', padding: '12px', background: validationResults.valid ? `${T.g}10` : `${T.d}10`, borderRadius: '8px', border: `1px solid ${validationResults.valid ? T.g : T.d}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              {validationResults.valid ? <CheckCircle2 size={16} color={T.g} /> : <AlertTriangle size={16} color={T.d} />}
              <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                {validationResults.valid ? 'Validation Passed' : 'Validation Issues Found'}
              </span>
            </div>
            {validationResults.issues && validationResults.issues.length > 0 && (
              <div style={{ fontSize: '0.75rem', color: T.d }}>
                {validationResults.issues.map((issue, i) => (
                  <div key={i}>• {issue}</div>
                ))}
              </div>
            )}
            {validationResults.warnings && validationResults.warnings.length > 0 && (
              <div style={{ fontSize: '0.75rem', color: T.w }}>
                {validationResults.warnings.map((warning, i) => (
                  <div key={i}>⚠️ {warning}</div>
                ))}
              </div>
            )}
            {validationResults.suggestions && validationResults.suggestions.length > 0 && (
              <div style={{ fontSize: '0.75rem', color: T.t2 }}>
                {validationResults.suggestions.map((suggestion, i) => (
                  <div key={i}>💡 {suggestion}</div>
                ))}
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
          <button style={{ ...S.btn, ...S.bSuc }} onClick={exportData}>
            <Download size={16} />
            Export All Artifacts (ZIP)
          </button>
          <button style={{ ...S.btn, ...S.bPP }} onClick={() => {
            if (onDataGenerated) {
              onDataGenerated(generatedData);
            }
          }}>
            <FileCode size={16} />
            Use in PLMXML Studio
          </button>
          <button style={{ ...S.btn, ...S.bGho }} onClick={() => setShowResults(false)}>
            <EyeOff size={16} />
            Hide Results
          </button>
        </div>
      </div>
    );
  };

  // ============================================
  // MAIN RENDER
  // ============================================
  return (
    <div style={S.container}>
      {renderStepIndicator()}
      {renderProductInput()}
      {renderResults()}

      {/* AI Assistant Modal */}
      {showAiAssistant && renderAIAssistantModal()}

      {/* Toast Notification */}
      {toast && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          padding: '12px 20px',
          borderRadius: '8px',
          background: toast.type === 'error' ? T.d : (toast.type === 'warning' ? T.w : T.g),
          color: '#fff',
          fontWeight: 600,
          fontSize: '0.85rem',
          zIndex: 2000,
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          animation: 'slideIn 0.3s ease'
        }}>
          {toast.message}
        </div>
      )}

      <style>{`
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .scrollable {
          max-height: 300px;
          overflow-y: auto;
        }
        .scrollable::-webkit-scrollbar {
          width: 6px;
        }
        .scrollable::-webkit-scrollbar-track {
          background: ${T.s};
          border-radius: 3px;
        }
        .scrollable::-webkit-scrollbar-thumb {
          background: ${T.b};
          border-radius: 3px;
        }
        .scrollable::-webkit-scrollbar-thumb:hover {
          background: ${T.t3};
        }
      `}</style>
    </div>
  );
};

// ============================================
// HELPER FUNCTIONS
// ============================================
const extractAllOperations = (bopData) => {
  if (!bopData) return [];
  
  if (bopData.productBOP) {
    const allOps = [];
    (bopData.productBOP.processes || []).forEach(process => {
      (process.plantBOPs || []).forEach(plantBOP => {
        (plantBOP.functionalGroups || []).forEach(fg => {
          (fg.operations || []).forEach(op => {
            allOps.push({
              ...op,
              functionalGroup: fg.name,
              plant: plantBOP.plant,
              process: process.name,
              processSequence: process.sequence,
              plantBOPName: plantBOP.name,
              fgSequence: fg.sequence,
              processType: process.type,
              plantBOPDescription: plantBOP.description
            });
          });
        });
      });
    });
    return allOps;
  }
  
  return (bopData.functionalGroups || []).flatMap(fg => 
    (fg.operations || []).map(op => ({
      ...op,
      functionalGroup: fg.name,
      plant: fg.plant || 'N/A'
    }))
  );
};

export default DEManyproduct;